import IUpdateSolarPlantSiteDetailsRequestDTO from "../../../shared/oversight-core/dtos/request-dtos/update-solar-plant-site-details-request-dto";
import IViewAdminSolarPlantAlertsRequestDTO from "../../../shared/oversight-core/dtos/request-dtos/view-admin-solar-plant-alerts-request-dto";
import IViewCustomerSolarPlantGraphRequestDTO from "../../../shared/oversight-core/dtos/request-dtos/view-customer-solar-plant-graph-request-dto";
import IViewUserSolarPlantDailyPowerRequestDTO from "../../../shared/oversight-core/dtos/request-dtos/view-user-solar-plant-daily-power-request-dto";
import IViewUserSolarPlantEnergyRequestDTO from "../../../shared/oversight-core/dtos/request-dtos/view-user-solar-plant-energy-request-dto";
import IAddSolarPlantChangelogEntryResponseDTO from "../../../shared/oversight-core/dtos/response-dtos/add-solar-plant-change-log-entry-response-dto";
import IUpdateSolarPlantSiteDetailsResponseDTO from "../../../shared/oversight-core/dtos/response-dtos/update-solar-plant-site-details-response-dto";
import IViewAdminSolarPlantAlertsResponseDTO from "../../../shared/oversight-core/dtos/response-dtos/view-admin-solar-plant-alerts-response-dto";
import IViewAllGenerationServiceProvidersResponseDTO from "../../../shared/oversight-core/dtos/response-dtos/view-all-generation-service-providers-response-dto";
import IViewCustomerPlantStorageDataResponseDTO from "../../../shared/oversight-core/dtos/response-dtos/view-customer-plant-storage-data-response-dto";
import IViewCustomerSolarPlantGraphResponseDTO from "../../../shared/oversight-core/dtos/response-dtos/view-customer-solar-plant-graph-response-dto";
import IViewUserSolarPlantDetailsResponseDTO from "../../../shared/oversight-core/dtos/response-dtos/view-user-solar-plant-details-response-dto";
import IViewUserSolarPlantEnergyResponseDTO from "../../../shared/oversight-core/dtos/response-dtos/view-user-solar-plant-energy-response-dto";
import IViewUserSolarPlantPowerResponseDTO from "../../../shared/oversight-core/dtos/response-dtos/view-user-solar-plant-power-response-dto";
import IViewUserSolarPlantInvertersResponseDTO from "../../../shared/oversight-core/dtos/response-dtos/view-user-solar-plantInverters-response-dto";
import { EExportFleetReportFormat } from "../../../shared/oversight-core/enums/export-fleet-report-format";
import { transformErrorResponse } from "../../../shared/oversight-core/utils/transformErrorResponse";
import { rootApi } from "../apiManager";
import { toQueryString } from "../query-string";

export const generationAPI = rootApi.injectEndpoints({
  endpoints: (builder) => ({
    viewCustomerPlantDetails: builder.query<
      IViewUserSolarPlantDetailsResponseDTO,
      {
        plantId: string;
      }
    >({
      transformErrorResponse,
      query({ plantId }) {
        return {
          url: `admin/generation-service-provider/${plantId}/view`,
          method: "GET",
        };
      },
    }),
    localViewCustomerPlantDetails: builder.query<
      IViewUserSolarPlantDetailsResponseDTO,
      {
        plantId: string;
      }
    >({
      transformErrorResponse,
      query({ plantId }) {
        return {
          url: `admin/generation-service-provider/${plantId}/local-view`,
          method: "GET",
        };
      },
    }),
    viewUserPlantDailyPower: builder.query<
      IViewUserSolarPlantPowerResponseDTO,
      IViewUserSolarPlantDailyPowerRequestDTO
    >({
      transformErrorResponse,
      query(data: IViewUserSolarPlantDailyPowerRequestDTO) {
        return {
          url: `admin/generation-service-provider/${data.plantId}/power/view`,
          method: "POST",
          body: {
            year: data.year,
            month: data.month,
            day: data.day,
          },
        };
      },
    }),
    viewUserPlantGeneration: builder.query<
      IViewUserSolarPlantEnergyResponseDTO,
      IViewUserSolarPlantEnergyRequestDTO
    >({
      transformErrorResponse,
      query(data: IViewUserSolarPlantEnergyRequestDTO) {
        return {
          url: `admin/generation-service-provider/${data.plantId}/energy-generation/view`,
          method: "POST",
          body: {
            year: data.year,
            month: data.month,
            day: data.day,
            viewType: data.viewType,
          },
        };
      },
    }),
    viewUserPlantInverters: builder.query<
      IViewUserSolarPlantInvertersResponseDTO,
      {
        plantId: string;
        pageNumber?: number;
        pageSize?: number;
        sortBy?: string;
        ascending?: boolean;
      }
    >({
      transformErrorResponse,
      query({ plantId, pageNumber, pageSize, sortBy, ascending }) {
        return {
          url: `admin/generation-service-provider/${plantId}/inverters/view`,
          method: "GET",
          params: {
            pageNumber: pageNumber,
            pageSize: pageSize,
            sortBy: sortBy,
            ascending: ascending,
          },
        };
      },
    }),
    viewGenerationServiceProviders: builder.query<
      IViewAllGenerationServiceProvidersResponseDTO,
      void
    >({
      transformErrorResponse,
      query() {
        return {
          url: `energy-generation/generation-service-provider/list`,
          method: "GET",
        };
      },
    }),
    viewSolarPlantAlertsAdmin: builder.query<
      IViewAdminSolarPlantAlertsResponseDTO,
      IViewAdminSolarPlantAlertsRequestDTO
    >({
      transformErrorResponse,
      query({
        plantId,
        pageNumber,
        pageSize,
        sortBy,
        ascending,
        start,
        end,
        severity,
        alertState,
        alertName,
      }) {
        return {
          url: `admin/generation-service-provider/${plantId}/alerts${toQueryString(
            {
              pageNumber,
              pageSize,
              sortBy,
              ascending,
              start,
              end,
              severity,
              alertState,
              alertName,
            },
          )}`,
          method: "GET",
        };
      },
    }),
    viewCustomerPlantGraphsAdmin: builder.query<
      IViewCustomerSolarPlantGraphResponseDTO,
      IViewCustomerSolarPlantGraphRequestDTO
    >({
      transformErrorResponse,
      query({ plantId, startDate, endDate, viewType, graphType }) {
        return {
          url: `admin/generation-service-provider/${plantId}/graph/view`,
          method: "GET",
          params: {
            startDate,
            endDate,
            viewType,
            graphType,
          },
        };
      },
    }),
    changeSolarPlantStatus: builder.mutation<void, { plantId: string }>({
      transformErrorResponse,
      query({ plantId }) {
        return {
          url: `admin/generation-service-provider/solar-plant/${plantId}/change-status`,
          method: "PUT",
        };
      },
    }),
    updateSolarPlantSiteDetails: builder.mutation<
      IUpdateSolarPlantSiteDetailsResponseDTO,
      IUpdateSolarPlantSiteDetailsRequestDTO
    >({
      transformErrorResponse,
      query(data) {
        return {
          url: `admin/generation-service-provider/${data.plantId}/site-details`,
          method: "PUT",
          body: {
            siteName: data.siteName,
            siteAlias: data.siteAlias,
            address: data.address,
            latitude: data.latitude,
            longitude: data.longitude,
            capacity: data.capacity,
          },
        };
      },
    }),
    addSolarPlantChangelogEntry: builder.mutation<
      IAddSolarPlantChangelogEntryResponseDTO,
      { plantId: string; note: string }
    >({
      transformErrorResponse,
      query({ plantId, note }) {
        return {
          url: `admin/generation-service-provider/${plantId}/changelog`,
          method: "POST",
          body: {
            note,
          },
        };
      },
    }),
    viewCustomerPlantStorageData: builder.mutation<
      IViewCustomerPlantStorageDataResponseDTO,
      { plantId: string; deviceSn: string }
    >({
      transformErrorResponse,
      query({ plantId, deviceSn }) {
        return {
          url: `admin/generation-service-provider/${plantId}/storage-data/view`,
          method: "POST",
          body: {
            deviceSn,
          },
        };
      },
    }),
    exportFleetReport: builder.query<
      Blob,
      {
        startDate: string;
        endDate: string;
        viewType: string;
        format?: EExportFleetReportFormat;
        generationServiceProviderId?: string | null;
      }
    >({
      query: ({
        startDate,
        endDate,
        viewType,
        format,
        generationServiceProviderId,
      }) => ({
        url: `admin/generation-service-provider/export/fleet`,
        method: "GET",
        params: {
          startDate,
          endDate,
          viewType,
          format,
          generationServiceProviderId,
        },
        responseHandler: (response) => response.blob(),
      }),
      transformErrorResponse,
    }),
    exportFleetReportById: builder.query<
      Blob,
      {
        plantId: string;
        startDate: string;
        endDate: string;
        viewType: string;
        format?: EExportFleetReportFormat;
      }
    >({
      query: ({ plantId, startDate, endDate, viewType, format }) => ({
        url: `admin/generation-service-provider/${plantId}/export`,
        method: "GET",
        params: {
          startDate,
          endDate,
          viewType,
          format,
        },
        responseHandler: (response) => response.blob(),
      }),
      transformErrorResponse,
    }),
  }),
  overrideExisting: false,
});

export const {
  useLazyViewCustomerPlantDetailsQuery,
  useLazyLocalViewCustomerPlantDetailsQuery,
  useLazyViewUserPlantDailyPowerQuery,
  useLazyViewUserPlantGenerationQuery,
  useLazyViewUserPlantInvertersQuery,
  useLazyViewGenerationServiceProvidersQuery,
  useLazyViewSolarPlantAlertsAdminQuery,
  useLazyViewCustomerPlantGraphsAdminQuery,
  useChangeSolarPlantStatusMutation,
  useUpdateSolarPlantSiteDetailsMutation,
  useAddSolarPlantChangelogEntryMutation,
  useViewCustomerPlantStorageDataMutation,
  useExportFleetReportQuery,
  useExportFleetReportByIdQuery,
  useLazyExportFleetReportQuery,
  useLazyExportFleetReportByIdQuery,
} = generationAPI;
